import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkItemKind, Priority, WorkflowConfig } from '@reef/schema';
import { api, ConnectionsRequiredError, type RunSummary, type WorkflowSummary } from '../api.ts';
import { WorkflowGraph, countSteps } from '../components/WorkflowGraph.tsx';

/**
 * One factory workflow: what it takes in, what it has done, and what it does.
 *
 * The three panes are the three questions somebody arrives with. The run input
 * is on the left because starting a run is the reason most visits happen; the
 * recent runs sit under it because "has this worked before" is answered by the
 * same glance; and the graph is on the right because it is the thing you read
 * rather than fill in.
 *
 * The input form is generated from the factory's own work-item vocabulary
 * rather than from a free-text box. A work item typed at the door is a work
 * item every downstream step can rely on the shape of — which is the whole
 * argument for typing the handoffs in the first place, and it would be a
 * strange argument to make and then start every run with a paragraph.
 */

const KINDS: readonly WorkItemKind[] = [
  'bug',
  'feature',
  'incident',
  'documentation',
  'chore',
  'security',
];

const PRIORITIES: readonly Priority[] = ['P0', 'P1', 'P2', 'P3'];

interface WorkItemDraft {
  id: string;
  kind: WorkItemKind | '';
  title: string;
  description: string;
  repo: string;
  priority: Priority;
}

const EMPTY_DRAFT: WorkItemDraft = {
  id: '',
  kind: '',
  title: '',
  description: '',
  repo: '',
  priority: 'P2',
};

/** What actually goes to the run: the draft, as the work item the steps expect. */
function draftToWorkItem(draft: WorkItemDraft): Record<string, unknown> {
  return {
    id: draft.id.trim(),
    kind: draft.kind || 'bug',
    title: draft.title.trim(),
    description: draft.description.trim(),
    priority: draft.priority,
    repo: draft.repo.trim() || null,
    state: 'triaged',
    signal: {
      source: 'manual',
      externalId: draft.id.trim(),
      title: draft.title.trim(),
      observedAt: new Date().toISOString(),
    },
  };
}

export function FactoryWorkflowsView({
  workflows,
  factoryIds,
}: {
  workflows: WorkflowSummary[];
  /** Ids the control plane says are part of the factory, in loop order. */
  factoryIds: string[];
}) {
  /*
   * The factory's own workflows first and in the order work moves through them,
   * then everything else. A studio that sorted these alphabetically would put
   * validation before triage, which is the one ordering that misleads.
   */
  const ordered = useMemo(() => {
    const byId = new Map(workflows.map((workflow) => [workflow.id, workflow]));
    const factory = factoryIds.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []));
    const rest = workflows.filter((workflow) => !factoryIds.includes(workflow.id));
    return { factory, rest };
  }, [workflows, factoryIds]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [workflow, setWorkflow] = useState<WorkflowConfig | null>(null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [mode, setMode] = useState<'form' | 'json'>('form');
  const [draft, setDraft] = useState<WorkItemDraft>(EMPTY_DRAFT);
  const [json, setJson] = useState('{\n  \n}');
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedId || workflows.length === 0) return;
    setSelectedId(ordered.factory[0]?.id ?? workflows[0]!.id);
  }, [selectedId, workflows, ordered]);

  const refreshRuns = useCallback((id: string) => {
    api
      .listRuns(id)
      .then((result) => setRuns(result.runs))
      .catch(() => setRuns([]));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    let current = true;

    setWorkflow(null);
    setError(null);
    setStarted(null);

    api
      .getWorkflow(selectedId)
      .then((result) => {
        if (!current) return;
        setWorkflow(result.workflow);
        // Pre-fill the id the way a person would have to anyway, so the common
        // case is three fields rather than four.
        setDraft((previous) => ({
          ...previous,
          id: previous.id || `wi-${Math.random().toString(36).slice(2, 8)}`,
        }));
      })
      .catch((caught) => current && setError((caught as Error).message));

    refreshRuns(selectedId);

    return () => {
      current = false;
    };
  }, [selectedId, refreshRuns]);

  // Kept in sync one way only: switching to JSON shows what the form built.
  // Switching back does not parse the JSON into the form, because a hand-edited
  // payload with fields the form has no input for would silently lose them.
  const switchMode = (next: 'form' | 'json') => {
    if (next === 'json') setJson(JSON.stringify(draftToWorkItem(draft), null, 2));
    setMode(next);
  };

  const run = async () => {
    if (!selectedId) return;
    setStarting(true);
    setError(null);
    setStarted(null);

    try {
      const payload = mode === 'form' ? draftToWorkItem(draft) : (JSON.parse(json) as unknown);
      const result = await api.runWorkflow(selectedId, JSON.stringify(payload, null, 2));
      setStarted(result.runId);
      refreshRuns(selectedId);
    } catch (caught) {
      setError(
        caught instanceof ConnectionsRequiredError
          ? `${caught.message} ${caught.missing.map((entry) => entry.connectionName).join(', ')}`
          : (caught as Error).message,
      );
    } finally {
      setStarting(false);
    }
  };

  const valid = mode === 'json' || (draft.id.trim() && draft.kind && draft.title.trim());

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Workflows</span>
          <span className="studio-crumb-sep">/</span>
          <select
            className="studio-crumb-select"
            aria-label="Workflow"
            value={selectedId ?? ''}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <optgroup label="Software factory">
              {ordered.factory.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.id}
                </option>
              ))}
            </optgroup>
            {ordered.rest.length > 0 && (
              <optgroup label="Other workflows">
                {ordered.rest.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.id}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </span>

        <span className="spacer" />

        <a className="studio-head-link" href="/api/v1/workflows" target="_blank" rel="noreferrer">
          API endpoints
        </a>
        <a
          className="studio-head-link"
          href="https://github.com/MartinRoberts-Fountain/reef/blob/main/docs/factory/README.md"
          target="_blank"
          rel="noreferrer"
        >
          Factory documentation
        </a>
      </header>

      <div className="studio-split">
        <div className="studio-column">
          <section className="studio-card">
            <div className="studio-card-head">
              <h2>{workflow?.id ?? selectedId ?? 'No workflow'}</h2>
              {workflow && <span className="chip">{countSteps(workflow.steps)} steps</span>}
            </div>

            <div className="studio-card-body">
              <div className="studio-row">
                <p className="section-title" style={{ margin: 0 }}>
                  Run input
                </p>
                <div className="toggle" role="group" aria-label="Input format">
                  <button
                    type="button"
                    aria-pressed={mode === 'form'}
                    onClick={() => switchMode('form')}
                  >
                    Form
                  </button>
                  <button
                    type="button"
                    aria-pressed={mode === 'json'}
                    onClick={() => switchMode('json')}
                  >
                    {'{ } JSON'}
                  </button>
                </div>
              </div>

              {mode === 'form' ? (
                <div className="studio-form">
                  <label>
                    <span>
                      Id <span className="required">*</span>
                    </span>
                    <input
                      value={draft.id}
                      onChange={(event) => setDraft({ ...draft, id: event.target.value })}
                    />
                  </label>

                  <label>
                    <span>
                      Type <span className="required">*</span>
                    </span>
                    <select
                      value={draft.kind}
                      onChange={(event) =>
                        setDraft({ ...draft, kind: event.target.value as WorkItemKind })
                      }
                    >
                      <option value="">Select an option</option>
                      {KINDS.map((kind) => (
                        <option key={kind} value={kind}>
                          {kind}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>
                      Title <span className="required">*</span>
                    </span>
                    <input
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    />
                  </label>

                  <label>
                    Description
                    <input
                      value={draft.description}
                      onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                    />
                  </label>

                  <label>
                    Repo
                    <input
                      placeholder="acme/web"
                      value={draft.repo}
                      onChange={(event) => setDraft({ ...draft, repo: event.target.value })}
                    />
                  </label>

                  <label>
                    Priority
                    <select
                      value={draft.priority}
                      onChange={(event) =>
                        setDraft({ ...draft, priority: event.target.value as Priority })
                      }
                    >
                      {PRIORITIES.map((priority) => (
                        <option key={priority} value={priority}>
                          {priority}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : (
                <textarea
                  className="studio-json"
                  rows={14}
                  value={json}
                  onChange={(event) => setJson(event.target.value)}
                  spellCheck={false}
                />
              )}

              <div className="studio-row">
                <button className="primary" disabled={!valid || starting || !workflow} onClick={run}>
                  {starting ? 'Starting…' : 'Run workflow'}
                </button>
                {started && <span className="hint">Started {started}</span>}
              </div>

              {error && <div className="banner danger">{error}</div>}
            </div>
          </section>

          <section className="studio-card">
            <div className="studio-card-head">
              <h2>Recent runs</h2>
            </div>
            <div className="studio-card-body">
              {runs.length === 0 ? (
                <p className="hint">Your run history will appear here once you run the workflow.</p>
              ) : (
                <ul className="studio-runs">
                  {runs.slice(0, 12).map((entry) => (
                    <li key={entry.id}>
                      <span className={`run-dot ${entry.status}`} aria-hidden="true" />
                      <span className="studio-run-id">{entry.id}</span>
                      <span className="hint">{entry.triggerType}</span>
                      <span className="hint studio-run-status">{entry.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        <section className="studio-canvas">
          {workflow ? (
            <WorkflowGraph workflow={workflow} />
          ) : (
            <div className="empty">
              {error ? 'That workflow could not be loaded.' : 'Loading the workflow…'}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
